/**
 * Custom hook for loading linkedTable metadata using TanStack Query
 * Replaces useLinkedTableLoader with better caching and deduplication
 * Uses snake_case types directly from API (no transformations)
 */

import { useQuery } from '@tanstack/react-query';
import type {
  LinkedTableSourceApi,
  LinkedTableJoinApi,
  LinkedTableFilterApi,
  LinkedTableCalculatedColumnApi,
  LinkedTableValueApi,
} from '@loader/shared-types';

import { useRpc } from '@/lib/rpc';
import { logger } from '@/lib/logger';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Linked table API response
 * Matches the database schema from apps/api/src/db/schema.ts
 */
interface LinkedTableApiResponse {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  tables: LinkedTableSourceApi[];
  joins: LinkedTableJoinApi[] | null;
  filters: LinkedTableFilterApi[] | null;
  calculated_columns: LinkedTableCalculatedColumnApi[] | null;
  row_groups: string[] | null;
  values: LinkedTableValueApi[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface UseLinkedTableMetadataProps {
  id: string | undefined;
  enabled?: boolean;
}

interface UseLinkedTableMetadataResult {
  linkedTable: LinkedTableApiResponse | null;
  /** Name of the first table (for display purposes) */
  primaryTableName: string;
}

/**
 * Hook for loading linkedTable metadata and table name
 * Uses TanStack Query for automatic caching and deduplication
 * Errors are thrown and handled by TanStack Router error boundary
 */
export const useLinkedTableMetadata = ({
  id,
  enabled = true,
}: UseLinkedTableMetadataProps): UseLinkedTableMetadataResult => {
  const rpc = useRpc();

  // Query for linkedTable metadata
  const { data: linkedTableData } = useQuery({
    queryKey: queryKeys.linkedTables.detail(id || ''),
    queryFn: async () => {
      if (!id) {
        throw new Error('LinkedTable ID is required');
      }
      logger.info('Loading linkedTable metadata', { linkedTableId: id });
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private['linked-tables'][':id'].$get({
        param: { id }
      });
      if (!res.ok) throw new Error('Failed to load linkedTable');
      const response = await res.json();
      return response.linkedTable;
    },
    enabled: enabled && !!id,
    staleTime: 60 * 1000, // 1 minute - linkedTable metadata doesn't change often
  });

  // Query for primary table name (dependent on linkedTable having tables[0].target_id)
  const primaryTableId = linkedTableData?.tables?.[0]?.target_id;
  const { data: tableData } = useQuery({
    queryKey: queryKeys.tables.detail(primaryTableId || ''),
    queryFn: async () => {
      if (!primaryTableId) {
        throw new Error('LinkedTable has no tables');
      }
      logger.debug('Fetching primary table name', {
        tableId: primaryTableId,
      });
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.tables[':id'].$get({
        param: { id: primaryTableId }
      });
      if (!res.ok) throw new Error('Failed to load table');
      const response = await res.json();
      return response.table.name;
    },
    enabled: enabled && !!primaryTableId,
    staleTime: 5 * 60 * 1000, // 5 minutes - table names don't change
  });

  return {
    linkedTable: linkedTableData || null,
    primaryTableName: tableData || '',
  };
};
